// Candidate filter schema — type-driven, mirrors server/filters.js CANDIDATE_REGISTRY.
// The rail derives the control from `type`; enum fields carry options or a dropdown key.

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'parsing', label: 'Parsing' },
  { value: 'enriched', label: 'Enriched' },
  { value: 'invited_to_interview', label: 'Invited to interview' },
  { value: 'interviewing', label: 'Interviewing' },
  { value: 'assessed', label: 'Assessed' },
  { value: 'available', label: 'Available' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'placed', label: 'Placed' },
  { value: 'archived', label: 'Archived' },
];

const INTAKE_OPTIONS = [
  { value: 'self_serve', label: 'Self serve' },
  { value: 'bulk_import', label: 'Bulk import' },
  { value: 'manual', label: 'Manual add' },
  { value: 'bulk_cv', label: 'Bulk CV parse' },
];

const RTW_OPTIONS = [
  { value: 'citizen', label: 'Citizen' },
  { value: 'permanent_resident', label: 'Permanent resident' },
  { value: 'visa_holder', label: 'Visa holder' },
  { value: 'needs_sponsorship', label: 'Needs sponsorship' },
  { value: 'unknown', label: 'Unknown' },
];

const CONSENT_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'explicit', label: 'Explicit' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

const VERIFICATION_OPTIONS = [
  { value: 'deliverable', label: 'Deliverable' },
  { value: 'risky', label: 'Risky' },
  { value: 'undeliverable', label: 'Undeliverable' },
  { value: 'unknown', label: 'Unknown' },
];

const RATE_UNIT_OPTIONS = [
  { value: 'year', label: 'Per year' },
  { value: 'day', label: 'Per day' },
  { value: 'hour', label: 'Per hour' },
];

export const CANDIDATE_SCHEMA = {
  groups: ['Candidate', 'Classification', 'Pipeline', 'CV & parsing', 'Compliance', 'Rate', 'Tags & notes', 'Dates'],
  fields: [
    // Candidate
    { key: 'full_name', label: 'Name', group: 'Candidate', type: 'text' },
    { key: 'location', label: 'Location', group: 'Candidate', type: 'text', dropdown: 'location' },
    { key: 'has_email', label: 'Has email', group: 'Candidate', type: 'boolean' },
    { key: 'has_phone', label: 'Has phone', group: 'Candidate', type: 'boolean' },
    { key: 'has_linkedin', label: 'Has LinkedIn', group: 'Candidate', type: 'boolean' },
    { key: 'has_github', label: 'Has GitHub', group: 'Candidate', type: 'boolean' },
    { key: 'email', label: 'Email', group: 'Candidate', type: 'text' },
    // Classification
    { key: 'expertise_domain', label: 'Expertise domain', group: 'Classification', type: 'enum', dropdown: 'expertise_domain' },
    { key: 'current_company', label: 'Company', group: 'Classification', type: 'text', dropdown: 'current_company' },
    { key: 'current_title', label: 'Title', group: 'Classification', type: 'text' },
    { key: 'headline', label: 'Headline', group: 'Classification', type: 'text' },
    // Pipeline
    { key: 'status', label: 'Status', group: 'Pipeline', type: 'enum', options: STATUS_OPTIONS },
    { key: 'intake_lane', label: 'Intake lane', group: 'Pipeline', type: 'enum', options: INTAKE_OPTIONS },
    { key: 'enriched', label: 'Enriched', group: 'Pipeline', type: 'boolean' },
    { key: 'enriched_at', label: 'Enriched date', group: 'Pipeline', type: 'date' },
    // CV & parsing
    { key: 'has_cv', label: 'Has CV on file', group: 'CV & parsing', type: 'boolean' },
    { key: 'has_parse_flags', label: 'Parse flags for review', group: 'CV & parsing', type: 'boolean' },
    { key: 'email_verification_result', label: 'Email verification', group: 'CV & parsing', type: 'enum', options: VERIFICATION_OPTIONS },
    // Compliance
    { key: 'right_to_work_status', label: 'Right to work', group: 'Compliance', type: 'enum', options: RTW_OPTIONS },
    { key: 'right_to_work_country', label: 'RTW country', group: 'Compliance', type: 'text', dropdown: 'right_to_work_country' },
    { key: 'consent_status', label: 'Consent', group: 'Compliance', type: 'enum', options: CONSENT_OPTIONS },
    { key: 'retention_expires_at', label: 'Retention expiry', group: 'Compliance', type: 'date' },
    // Rate
    { key: 'expected_rate_gbp', label: 'Expected rate (GBP)', group: 'Rate', type: 'number', min: 0, max: 500000 },
    { key: 'expected_rate_unit', label: 'Rate unit', group: 'Rate', type: 'enum', options: RATE_UNIT_OPTIONS },
    // Tags & notes
    { key: 'tag', label: 'Tag', group: 'Tags & notes', type: 'enum', dropdown: 'tag' },
    { key: 'has_notes', label: 'Has notes', group: 'Tags & notes', type: 'exists' },
    // Interviews
    { key: 'has_interview', label: 'Has an interview', group: 'Interviews', type: 'exists' },
    { key: 'interview_band', label: 'Interview band', group: 'Interviews', type: 'enum', options: [{ value: 'strong', label: 'Strong' }, { value: 'possible', label: 'Possible' }, { value: 'weak', label: 'Weak' }] },
    { key: 'interview_score', label: 'Interview score (%)', group: 'Interviews', type: 'number', min: 0, max: 100 },
    // Dates
    // C1-EMAIL: temperature and the two dates behind it
    { key: 'temperature', label: 'Temperature', group: 'Contact', type: 'enum', options: [{ value: 'hot', label: 'Hot' }, { value: 'warm', label: 'Warm' }, { value: 'cold', label: 'Cold' }, { value: 'never', label: 'Never contacted' }] },
    { key: 'last_contacted_at', label: 'Last contacted', group: 'Contact', type: 'date' },
    { key: 'last_responded_at', label: 'Last responded', group: 'Contact', type: 'date' },
    { key: 'created_at', label: 'Added date', group: 'Dates', type: 'date' },
    { key: 'updated_at', label: 'Modified date', group: 'Dates', type: 'date' },
  ],
  quick: ['status', 'temperature', 'expertise_domain', 'has_parse_flags', 'tag'], // C1-EMAIL: temperature
};

export const DATE_PRESETS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

const _schema = { CANDIDATE_SCHEMA, DATE_PRESETS };
export default _schema;
